import { useEffect, useState } from "react";
import { AlertTriangle, Image as ImageIcon, Plus, Trash2, X } from "lucide-react";
import { Button } from "./Button";

const formatSize = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Model-agnostic image editor over URL strings.
// value: string[]; onChange(nextUrls); activeUrl highlights one; renderBadge(url) optional chip.
// suspiciousUrls: string[] of known placeholders — flagged tiles get a warning badge/ring and a
//   one-click "Remove all placeholders" button (same purpose as the image-review resolve view).
export default function ImageListEditor({
  value = [],
  onChange,
  activeUrl,
  mobileActiveUrl,
  onSelectDesktop,
  onSelectMobile,
  onUseAutomaticDesktop,
  onUseAutomaticMobile,
  desktopAutomatic = false,
  mobileAutomatic = false,
  renderBadge,
  suspiciousUrls = [],
  title = "Images",
}) {
  const [newImageUrl, setNewImageUrl] = useState("");
  const [imageMeta, setImageMeta] = useState({});

  const urls = (value || []).filter((u) => u);
  const suspiciousSet = new Set(suspiciousUrls);
  const hasSuspicious = urls.some((u) => suspiciousSet.has(u));
  // Stable key over the URL set so the meta-fetch effect re-runs only when the
  // actual images change — not on every parent render (callers pass a fresh array each time).
  const urlsKey = JSON.stringify(urls);

  useEffect(() => {
    urls.forEach((url) => {
      if (imageMeta[url]) return;
      const img = new window.Image();
      img.onload = () =>
        setImageMeta((prev) => ({
          ...prev,
          [url]: { ...prev[url], width: img.naturalWidth, height: img.naturalHeight, error: false },
        }));
      img.onerror = () => setImageMeta((prev) => ({ ...prev, [url]: { ...prev[url], error: true } }));
      img.src = url;
      fetch(url, { method: "HEAD", mode: "cors" })
        .then((res) => {
          const len = res.headers.get("content-length");
          if (len) setImageMeta((prev) => ({ ...prev, [url]: { ...prev[url], size: Number(len) } }));
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlsKey]);

  const handleAdd = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    if (urls.includes(url)) {
      setNewImageUrl("");
      return;
    }
    // Prepend so the newly added image leads (matches event behavior: images[0] is the source credit).
    onChange([url, ...urls]);
    setNewImageUrl("");
  };

  const handleRemove = (idx) => onChange(urls.filter((_, i) => i !== idx));
  // Drop every flagged placeholder in one click (keeps clean images), so the admin
  // doesn't have to remove them one by one.
  const handleRemoveAllSuspicious = () => onChange(urls.filter((u) => !suspiciousSet.has(u)));
  const urlKeyCounts = new Map();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
          <span className="ml-2 text-xs font-normal normal-case tracking-normal">({urls.length})</span>
        </h4>
        {hasSuspicious && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={handleRemoveAllSuspicious}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove all placeholders
          </Button>
        )}
      </div>
      {(onSelectDesktop || onSelectMobile) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
          <span className="font-medium text-foreground">Display selection</span>
          {onUseAutomaticDesktop && (
            <Button
              type="button"
              variant={desktopAutomatic ? "default" : "outline"}
              size="sm"
              onClick={onUseAutomaticDesktop}
            >
              Desktop: automatic
            </Button>
          )}
          {onUseAutomaticMobile && (
            <Button
              type="button"
              variant={mobileAutomatic ? "default" : "outline"}
              size="sm"
              onClick={onUseAutomaticMobile}
            >
              Mobile: automatic
            </Button>
          )}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {urls.map((url, idx) => {
          const occurrence = urlKeyCounts.get(url) || 0;
          urlKeyCounts.set(url, occurrence + 1);
          const isActive = activeUrl != null && url === activeUrl;
          const isMobileActive = mobileActiveUrl != null && url === mobileActiveUrl;
          const isSuspicious = suspiciousSet.has(url);
          const meta = imageMeta[url] || {};
          return (
            <div
              key={`${url}:${occurrence}`}
              className={`relative group rounded-lg overflow-hidden border-2 bg-muted/30 ${
                isActive
                  ? "border-primary ring-2 ring-primary/30"
                  : isSuspicious
                    ? "border-destructive ring-2 ring-destructive/30"
                    : "border-border"
              }`}
            >
              <div className="relative">
                {meta.error ? (
                  <div className="flex w-full aspect-video bg-muted items-center justify-center">
                    <div className="text-center p-2">
                      <ImageIcon className="h-6 w-6 text-muted-foreground mx-auto mb-1" />
                      <p className="text-xs text-muted-foreground">Failed to load</p>
                    </div>
                  </div>
                ) : (
                  <a href={url} target="_blank" rel="noreferrer" title="Open image in a new tab">
                    <img
                      src={url}
                      alt={`Image ${idx + 1}`}
                      className="w-full aspect-video object-cover cursor-zoom-in"
                      onError={() =>
                        setImageMeta((prev) => ({ ...prev, [url]: { ...prev[url], error: true } }))
                      }
                    />
                  </a>
                )}
                {isActive && (
                  <span className="absolute top-1.5 left-1.5 bg-primary text-primary-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded">
                    Desktop
                  </span>
                )}
                {isMobileActive && (
                  <span className={`absolute ${isActive ? "top-8" : "top-1.5"} left-1.5 bg-sky-700 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded`}>
                    Mobile
                  </span>
                )}
                {isSuspicious && !isActive && (
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-destructive text-destructive-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded">
                    <AlertTriangle className="h-2.5 w-2.5" />
                    placeholder
                  </span>
                )}
                {renderBadge && <span className="absolute bottom-1.5 left-1.5">{renderBadge(url)}</span>}
                <button
                  type="button"
                  className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemove(idx)}
                  title="Remove image"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="px-2.5 py-2 space-y-1">
                <p className="text-xs text-muted-foreground truncate" title={url}>
                  {url.split("/").pop() || url}
                </p>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  {meta.width && meta.height && (
                    <span>
                      {meta.width} x {meta.height}
                    </span>
                  )}
                  {meta.width && meta.height && meta.size && <span className="text-border">|</span>}
                  {meta.size && <span>{formatSize(meta.size)}</span>}
                  {!meta.width && !meta.size && !meta.error && <span className="animate-pulse">Loading...</span>}
                  {meta.error && !meta.width && <span className="text-destructive">Unavailable</span>}
                </div>
                {(onSelectDesktop || onSelectMobile) && (
                  <div className="flex gap-1.5 pt-1">
                    {onSelectDesktop && (
                      <Button
                        type="button"
                        variant={!desktopAutomatic && isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => onSelectDesktop(url)}
                      >
                        Use desktop
                      </Button>
                    )}
                    {onSelectMobile && (
                      <Button
                        type="button"
                        variant={!mobileAutomatic && isMobileActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => onSelectMobile(url)}
                      >
                        Use mobile
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {urls.length === 0 && (
        <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg">
          No images added yet
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="url"
          value={newImageUrl}
          onChange={(e) => setNewImageUrl(e.target.value)}
          placeholder="https://... paste image URL"
          className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          onKeyDown={(e) => {
            if (e.key === "Enter" && newImageUrl.trim()) {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" disabled={!newImageUrl.trim()} onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}
